import { useCallback, useEffect, useRef, useState } from "react";
import {
  AnonymousBaseProgressContextProps,
  AnonymousProgressContextState,
  useAnonymousProgressContext,
} from "@/hooks/progress-context/use-anonymous-progress-provider";
import $api from "@/data/api.constants";

type WebsocketTaskType =
  | "conv"
  | "duet"
  | "split"
  | "vocalRemove"
  | "textToSpeech"
  | "textToMusic";

export type ActiveProcessInfo = {
  itemId?: string;
  userId: string;
} | null;

export interface AuthorizedProgressContextState
  extends AnonymousProgressContextState {
  userId?: string;
  setUserId: (userId: string) => void;
}

interface AuthorizedBaseProgressContextProps
  extends AnonymousBaseProgressContextProps {
  getActiveItemByUserId: () => Promise<ActiveProcessInfo>;
  taskType: WebsocketTaskType;
}

export const useServiceProgressContext = ({
  getActiveItemByUserId,
  taskType,
  ...props
}: AuthorizedBaseProgressContextProps) => {
  const [userId, setUserId] = useState<string>();

  const buildWebSocketUrlCallback = useCallback(
    (processId: string) => {
      return `${$api.serverWebsocket}${taskType}/${userId}/${processId}`;
    },
    [taskType, userId],
  );

  const { setProcessId, processId, initWebsocket, ...context } =
    useAnonymousProgressContext({
      ...props,
      buildWebSocketUrl: buildWebSocketUrlCallback,
    });

  const statusUseEffectInit = useRef(false);
  useEffect(() => {
    if (!statusUseEffectInit.current) {
      statusUseEffectInit.current = true;
    } else {
      return;
    }
    getActiveItemByUserId().then((result) => {
      if (!result) return;
      const { itemId, userId } = result;
      setProcessId(itemId);
      setUserId(userId);
    });
  }, []);

  useEffect(() => {
    if (userId) initWebsocket();
  }, [userId]);

  return {
    ...context,
    userId,
    setUserId,
    processId,
    setProcessId,
  };
};
